// Client-side helpers for Mahjong tile visuals + selectability rules.

export const TILE_LABELS = {
  dot_1: "1●", dot_2: "2●", dot_3: "3●", dot_4: "4●", dot_5: "5●",
  dot_6: "6●", dot_7: "7●", dot_8: "8●", dot_9: "9●",
  bam_1: "1❙", bam_2: "2❙", bam_3: "3❙", bam_4: "4❙", bam_5: "5❙",
  bam_6: "6❙", bam_7: "7❙", bam_8: "8❙", bam_9: "9❙",
  char_1: "一", char_2: "二", char_3: "三", char_4: "四", char_5: "五",
  char_6: "六", char_7: "七", char_8: "八", char_9: "九",
  wind_e: "東", wind_s: "南", wind_w: "西", wind_n: "北",
  dragon_red: "中", dragon_green: "發", dragon_white: "⬜",
  flower_run: "🏃", flower_yoga: "🧘", flower_lift: "🏋", flower_cycle: "🚴",
};

// Build a freeSet (Set<tile_id>) of currently selectable tiles. We still
// call the canonical `isSelectable` per tile so the half-step (0.5)
// coordinate tiles in some layouts stay correctly classified — but we
// build the set ONCE per render and pass it down so per-tile React.memo
// children can be cheaply re-rendered when only `selected` / `hint`
// changes. Tester complaint v35: "delayed selected tiles removing on
// complicated levels" was caused by the inline isSelectable scan
// firing for every tile on every parent re-render.
export function computeFreeSet(tiles) {
  // Iter76 — Perf fix. Build a spatial index ONCE per call so per-tile
  // selectability is O(1) instead of the old 3 × O(n) Array.some() scans.
  //
  // Iter84 — CORRECTNESS fix. The previous index rounded coordinates to
  // whole integers (`Math.round(x)`), which is WRONG for layouts whose
  // top tiles sit at half-coordinates (e.g. gym_fortress crown at x=2.5 /
  // 3.5). A half-offset tile physically straddles TWO tiles beneath it,
  // but rounding made it "belong" to only one cell — so tiles it partly
  // covered were rendered as free while the click-time `isSelectable`
  // (0.6-overlap geometry, identical to the backend solver) rejected the
  // tap → "can't remove tiles" on Iron Wall board 10. We now index on a
  // 0.5-grid (coords × 2) and replicate the exact 0.6-overlap semantics,
  // so the rendered free-set matches `isSelectable`/backend everywhere.
  const live = tiles.filter((t) => !t.removed);
  const cellZ = new Map(); // key "sx_sy" (scaled ×2) → Set<z>
  for (const t of live) {
    const key = `${Math.round(t.x * 2)}_${Math.round(t.y * 2)}`;
    let s = cellZ.get(key);
    if (!s) { s = new Set(); cellZ.set(key, s); }
    s.add(t.z);
  }
  const free = new Set();
  for (const t of live) {
    if (isSelectableIndexed(t, cellZ)) free.add(t.tile_id);
  }
  return free;
}

// Fast indexed selectability check mirroring the exact 0.6-overlap rules
// of the canonical `isSelectable` (and the backend `_is_selectable`).
// Coordinates are on a 0.5 grid, so |Δ| < 0.6 in real space ⇔ scaled
// offset ∈ {-1, 0, 1}. Total work is O(9) per tile → O(n) per board.
function isSelectableIndexed(tile, cellZ) {
  const sx = Math.round(tile.x * 2);
  const sy = Math.round(tile.y * 2);
  const z = tile.z;
  // Covered above? — any tile with z' > z whose centre overlaps ours.
  for (let ds = -1; ds <= 1; ds++) {
    for (let dt = -1; dt <= 1; dt++) {
      const s = cellZ.get(`${sx + ds}_${sy + dt}`);
      if (s) { for (const zz of s) if (zz > z) return false; }
    }
  }
  // Side blocked? — a same-z tile overlapping the (x∓1) neighbour cell.
  const sideBlocked = (baseSx) => {
    for (let ds = -1; ds <= 1; ds++) {
      for (let dt = -1; dt <= 1; dt++) {
        const s = cellZ.get(`${baseSx + ds}_${sy + dt}`);
        if (s && s.has(z)) return true;
      }
    }
    return false;
  };
  const leftBlocked = sideBlocked(sx - 2);
  const rightBlocked = sideBlocked(sx + 2);
  return !leftBlocked || !rightBlocked;
}

// A tile is selectable if:
// 1. NO tile at any layer ABOVE it overlaps its centre (top is fully clear), AND
// 2. either no tile at (x-1, y, z) (left clear) OR no tile at (x+1, y, z) (right clear)
//
// We use a strict <0.6 overlap threshold so partially-offset stacks are still
// detected as covering — this previously allowed Coach Tip to suggest a pair
// where one tile was visually hidden behind a higher-layer tile.
export function isSelectable(tile, tiles) {
  const { x, y, z } = tile;
  const at = (tx, ty, tz) =>
    tiles.some(
      (t) =>
        t.removed !== true &&
        t.tile_id !== tile.tile_id &&
        Math.abs(t.x - tx) < 0.6 &&
        Math.abs(t.y - ty) < 0.6 &&
        t.z === tz
    );
  // ANY tile in ANY layer above (z' > z) with overlapping centre covers us.
  const coveredAbove = tiles.some(
    (t) =>
      t.removed !== true &&
      t.tile_id !== tile.tile_id &&
      t.z > z &&
      Math.abs(t.x - x) < 0.6 &&
      Math.abs(t.y - y) < 0.6
  );
  if (coveredAbove) return false;
  const leftBlocked = at(x - 1, y, z);
  const rightBlocked = at(x + 1, y, z);
  return !leftBlocked || !rightBlocked;
}

// "Exposure score" — higher = more visually obvious. Used by findHint to pick
// the most user-friendly pair, not just any technically-valid pair.
//   +100 per z-layer (top crown tiles always preferred)
//   +20  for each free side (left or right)
//   -10  per neighbour tile in surrounding 3x3 footprint at same z
//
// Iter79 — Perf rewrite. Was doing 3 × O(n) `.some()` scans per call
// (~400 ops × every selectable tile = blocking on big boards). Now
// accepts a precomputed position index (`posMap` keyed by
// "x_y_z" → tile) so left/right/neighbour lookups are O(1). Total
// findHint cost drops from O(F·N) → O(F).
function _exposureScore(tile, posMap) {
  const { x, y, z } = tile;
  const xi = Math.round(x);
  const yi = Math.round(y);
  let score = z * 100;
  const leftBlocked = posMap.has(`${xi - 1}_${yi}_${z}`);
  const rightBlocked = posMap.has(`${xi + 1}_${yi}_${z}`);
  if (!leftBlocked) score += 20;
  if (!rightBlocked) score += 20;
  // Penalise tiles with many surrounding neighbours (visually crowded).
  // Check the 8 surrounding cells in the 3×3 footprint at same z.
  for (let dx = -1; dx <= 1; dx++) {
    for (let dy = -1; dy <= 1; dy++) {
      if (dx === 0 && dy === 0) continue;
      if (posMap.has(`${xi + dx}_${yi + dy}_${z}`)) score -= 10;
    }
  }
  return score;
}

// Build the position index once per call — same shape used by
// `computeFreeSet`. Exported for symmetry / future external use.
function _buildPosMap(tiles) {
  const posMap = new Map();
  for (const t of tiles) {
    if (t.removed) continue;
    const key = `${Math.round(t.x)}_${Math.round(t.y)}_${t.z}`;
    posMap.set(key, t);
  }
  return posMap;
}

export function findHint(tiles) {
  // Iter78 — Perf rewrite. Previous impl filtered with isSelectable
  // (O(n) per tile → O(n²) overall) AND called _exposureScore (also
  // O(n) per tile) inside an O(p²) pair-loop. On the 134-tile
  // spartan_shield board this could be ~40 000 ops per click when the
  // board was nearing deadlock and the auto-shuffle effect ran
  // findHint up to 3 times.
  // Iter79 — Further perf: build the position index ONCE and reuse it
  // across free-set + exposure-score lookups. Drops total cost from
  // O(n + f·n) → O(n + f).
  const free = computeFreeSet(tiles);
  const posMap = _buildPosMap(tiles);
  const scored = [];
  for (const t of tiles) {
    if (t.removed) continue;
    if (!free.has(t.tile_id)) continue;
    scored.push({ tile: t, score: _exposureScore(t, posMap) });
  }
  const groups = {};
  for (const s of scored) {
    (groups[s.tile.tile_type] = groups[s.tile.tile_type] || []).push(s);
  }
  // Score every candidate pair and return the best one — most exposed tiles
  // win, so the highlighted pair is always visually obvious to the user.
  let best = null;
  let bestScore = -Infinity;
  for (const arr of Object.values(groups)) {
    if (arr.length < 2) continue;
    for (let i = 0; i < arr.length; i++) {
      for (let j = i + 1; j < arr.length; j++) {
        const score = arr[i].score + arr[j].score;
        if (score > bestScore) {
          bestScore = score;
          best = [arr[i].tile.tile_id, arr[j].tile.tile_id];
        }
      }
    }
  }
  return best;
}

function _shuffleOnce(tiles) {
  const remaining = tiles.filter((t) => !t.removed);
  const types = remaining.map((t) => t.tile_type);
  // Fisher-Yates
  for (let i = types.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [types[i], types[j]] = [types[j], types[i]];
  }
  const map = new Map();
  remaining.forEach((t, idx) => map.set(t.tile_id, types[idx]));
  return tiles.map((t) =>
    t.removed ? t : { ...t, tile_type: map.get(t.tile_id) }
  );
}

function _countPairs(tiles) {
  // Iter78 — Use indexed free-set instead of the O(n²) isSelectable
  // filter; reseedSolvable can call this up to ~60 times per attempt
  // when retrying a stuck board, so this matters.
  const free = computeFreeSet(tiles);
  const counts = {};
  for (const t of tiles) {
    if (t.removed) continue;
    if (!free.has(t.tile_id)) continue;
    counts[t.tile_type] = (counts[t.tile_type] || 0) + 1;
  }
  return Object.values(counts).reduce((sum, c) => sum + Math.floor(c / 2), 0);
}

/**
 * Iter58 — SOLVABLE-RESEED.
 *
 * Tester demand: "Never auto-win. Boards must ALWAYS be clearable to the
 * very end by the player." The previous auto-shuffle could reach a state
 * where the only remaining tiles were structurally locked together and
 * the game would mercy-win. Bad UX (no satisfaction).
 *
 * This function searches for a VALID REMOVAL ORDER of the remaining tiles
 * (using randomised greedy: at each step pick 2 random currently-selectable
 * tiles, mark them removed, repeat). When a full order is found, the
 * search guarantees there's a way to clear the board. We then RE-TYPE the
 * tiles so consecutive pairs in the removal order share a tile_type,
 * pulling types from the existing multiset (which always has even counts
 * because pairs are removed together).
 *
 * Returns the re-typed `tiles` array on success, or `null` if no valid
 * removal order exists (truly dead position — extremely rare).
 */
function _findRemovalOrder(remaining, maxAttempts = 100) {
  // Iter66 — Tester perf: "reduce maxAttempts from 400 to 100 — solvability
  // is still reliable and the recovery path runs ~4x faster." The backend
  // forward-sim builder already guarantees solvability at deal time
  // (iter64), so this client-side recovery only fires on player-induced
  // dead states. 100 randomised attempts is more than enough to find a
  // valid removal order for any non-pathological remaining board.
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const inactive = new Set();
    const pairs = [];
    let stuck = false;
    while (inactive.size < remaining.length) {
      // Build active list with structural selectability.
      const active = remaining.filter((t) => !inactive.has(t.tile_id));
      const sel = active.filter((t) => _isStructurallySelectable(t, active));
      if (sel.length < 2) {
        stuck = true;
        break;
      }
      // Pick 2 distinct random selectables.
      const i1 = Math.floor(Math.random() * sel.length);
      let i2 = Math.floor(Math.random() * (sel.length - 1));
      if (i2 >= i1) i2 += 1;
      pairs.push([sel[i1].tile_id, sel[i2].tile_id]);
      inactive.add(sel[i1].tile_id);
      inactive.add(sel[i2].tile_id);
    }
    if (!stuck) return pairs;
  }
  return null;
}

// Structural selectability check that uses an ACTIVE-tile array instead of
// the full board (so we can simulate removal). Mirrors the rules of
// `isSelectable`: no tile covering above + at least one side free.
function _isStructurallySelectable(tile, active) {
  const { x, y, z } = tile;
  for (const t of active) {
    if (t.tile_id === tile.tile_id) continue;
    if (
      t.z > z &&
      Math.abs(t.x - x) < 0.6 &&
      Math.abs(t.y - y) < 0.6
    ) return false;
  }
  let leftBlocked = false, rightBlocked = false;
  for (const t of active) {
    if (t.tile_id === tile.tile_id) continue;
    if (t.z !== z) continue;
    if (Math.abs(t.y - y) > 0.6) continue;
    if (Math.abs(t.x - (x - 1)) < 0.6) leftBlocked = true;
    if (Math.abs(t.x - (x + 1)) < 0.6) rightBlocked = true;
  }
  return !leftBlocked || !rightBlocked;
}

export function reseedSolvable(tiles) {
  const remaining = tiles.filter((t) => !t.removed);
  if (remaining.length < 2) return tiles;
  // 1) Search for a valid removal order.
  const order = _findRemovalOrder(remaining);
  if (!order) return null;
  // 2) Build the type multiset from the remaining tiles.
  const typeCounts = new Map();
  for (const t of remaining) {
    typeCounts.set(t.tile_type, (typeCounts.get(t.tile_type) || 0) + 1);
  }
  // Flatten into a list of types — one slot per future pair.
  const pairTypes = [];
  for (const [type, count] of typeCounts.entries()) {
    for (let k = 0; k < Math.floor(count / 2); k++) pairTypes.push(type);
  }
  // If pairTypes length doesn't match order length (odd type counts —
  // shouldn't happen but guard), give up.
  if (pairTypes.length !== order.length) return null;
  // Shuffle the pair-type list so the same pair-type doesn't always sit
  // at the same position in the removal sequence.
  for (let i = pairTypes.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pairTypes[i], pairTypes[j]] = [pairTypes[j], pairTypes[i]];
  }
  // 3) Assign types: pair k of order → pairTypes[k].
  const assignment = new Map();
  for (let k = 0; k < order.length; k++) {
    const [idA, idB] = order[k];
    assignment.set(idA, pairTypes[k]);
    assignment.set(idB, pairTypes[k]);
  }
  return tiles.map((t) =>
    t.removed ? t : { ...t, tile_type: assignment.get(t.tile_id) ?? t.tile_type }
  );
}

/**
 * GUARANTEED-playable shuffle.
 *
 * Random Fisher-Yates shuffles can produce dead boards where all selectable
 * positions hold tile_types whose only matches are buried in lower layers.
 * To rule that out we forcibly pair up the free (selectable) positions:
 *   - find all currently selectable positions
 *   - take the available tile_type counts from the remaining pool
 *   - for each pair of free positions, pull a tile_type that still has at
 *     least 2 copies in the pool and assign BOTH free positions that type
 *   - distribute leftover types randomly across the buried positions
 *
 * Result: the board always has ⌊free_count / 2⌋ immediately-playable pairs.
 * Falls back to standard Fisher-Yates if the structural setup is
 * pathological (≤ 2 remaining tiles, only one type left, etc).
 */
function _guaranteedShuffle(tiles) {
  const remaining = tiles.filter((t) => !t.removed);
  if (remaining.length < 2) return tiles;

  // 1. Find all selectable positions on the CURRENT board layout.
  // Iter78 — use indexed free-set (O(n)) instead of O(n²) filter.
  const freeIds = computeFreeSet(remaining);
  if (freeIds.size < 2) {
    // No two free slots exist on the structural layout — random shuffle is
    // the best we can do.
    return _shuffleOnce(tiles);
  }

  // 2. Pool of available tile_types (multiset of all remaining types).
  const typeCounts = new Map();
  for (const t of remaining) {
    typeCounts.set(t.tile_type, (typeCounts.get(t.tile_type) || 0) + 1);
  }

  // 3. Assign tile_types to free positions.
  //
  // Iter45 fix: "after auto-reshuffle 4-5 pairs visible, makes game trivial."
  // Iter48 fix: "still 5 pairs visible, should be only 1."
  // Iter52 fix: tester complaint "Bootcamp rep 1 → 10 auto-reshuffles in
  //   a row." Root cause: guaranteeing EXACTLY 1 visible pair meant after
  //   the player matched it, often no other pair was reachable → another
  //   shuffle → infinite loop on small boards. Compromise: target 2–3
  //   visible pairs so the player has follow-through moves but the board
  //   doesn't look trivially solved.
  //   target = clamp(floor(freeSlots / 4), 2, 3)
  //     freeSlots 4–7 → 2 pairs ; 8+ → 3 pairs.
  const assignment = new Map(); // tile_id -> tile_type
  const freeArr = Array.from(freeIds);
  // Shuffle the order of free slots so the chosen pairs are picked randomly.
  for (let i = freeArr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [freeArr[i], freeArr[j]] = [freeArr[j], freeArr[i]];
  }
  // Decide how many guaranteed pairs we need to seed.
  const targetPairs = Math.max(
    2,
    Math.min(3, Math.floor(freeArr.length / 4))
  );
  const seededPairs = Math.min(
    targetPairs,
    Math.floor(freeArr.length / 2)
  );
  // Collect types with ≥2 copies in the pool.
  const pairableTypes = [];
  for (const [type, count] of typeCounts.entries()) {
    if (count >= 2) pairableTypes.push(type);
  }
  if (pairableTypes.length === 0 || freeArr.length < 2) {
    return _shuffleOnce(tiles);
  }
  // Pick `seededPairs` DISTINCT pairable types so we never accidentally
  // create more than one pair of the same type (which could create extra
  // matches with buried tiles later).
  for (let i = pairableTypes.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pairableTypes[i], pairableTypes[j]] = [pairableTypes[j], pairableTypes[i]];
  }
  const seededFreeTypes = new Set();
  let seedCursor = 0;
  for (let p = 0; p < seededPairs; p++) {
    if (seedCursor >= pairableTypes.length) break;
    const type = pairableTypes[seedCursor++];
    assignment.set(freeArr[p * 2], type);
    assignment.set(freeArr[p * 2 + 1], type);
    typeCounts.set(type, typeCounts.get(type) - 2);
    if (typeCounts.get(type) === 0) typeCounts.delete(type);
    seededFreeTypes.add(type);
  }
  const actualSeededPairs = Array.from(assignment.keys()).length / 2;

  // 4. Fill the rest of the free slots with DISTINCT non-seeded types so
  //    only the seeded pairs are matchable among free tiles. This keeps
  //    iter48's "no surprise extra pairs" guarantee while iter52 raises
  //    the seeded count from 1 to 2–3.
  const freeReservedTypes = new Set(seededFreeTypes);
  const remainingFreeArr = freeArr.slice(actualSeededPairs * 2);
  // Walk the remaining free slots and pick a unique type for each.
  // Prefer types with COUNT >= 1 not yet used among free slots.
  const candidatePool = [];
  for (const [type, count] of typeCounts.entries()) {
    if (count >= 1) candidatePool.push(type);
  }
  // Shuffle the candidate pool for variety.
  for (let i = candidatePool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [candidatePool[i], candidatePool[j]] = [candidatePool[j], candidatePool[i]];
  }
  let candCursor = 0;
  for (const slot of remainingFreeArr) {
    // Find next type not yet used in a free slot AND with remaining count.
    let assigned = false;
    while (candCursor < candidatePool.length) {
      const type = candidatePool[candCursor];
      candCursor++;
      if (freeReservedTypes.has(type)) continue;
      if ((typeCounts.get(type) || 0) < 1) continue;
      assignment.set(slot, type);
      freeReservedTypes.add(type);
      typeCounts.set(type, typeCounts.get(type) - 1);
      if (typeCounts.get(type) === 0) typeCounts.delete(type);
      assigned = true;
      break;
    }
    if (!assigned) {
      // Ran out of distinct types — fall through and the leftover-pool
      // step below will assign whatever's left (may create an extra
      // visible pair on extremely-multi-free boards, which is rare).
      break;
    }
  }

  // 5. Distribute leftover types to all unassigned positions (buried tiles +
  //    any odd-leftover free slot if freeIds count was odd or pool exhausted).
  const leftoverTypes = [];
  for (const [type, count] of typeCounts.entries()) {
    for (let k = 0; k < count; k++) leftoverTypes.push(type);
  }
  for (let i = leftoverTypes.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [leftoverTypes[i], leftoverTypes[j]] = [leftoverTypes[j], leftoverTypes[i]];
  }
  let cursor = 0;
  for (const t of remaining) {
    if (!assignment.has(t.tile_id)) {
      assignment.set(t.tile_id, leftoverTypes[cursor++]);
    }
  }

  return tiles.map((t) =>
    t.removed ? t : { ...t, tile_type: assignment.get(t.tile_id) }
  );
}

// Public shuffle — uses the guaranteed-playable algorithm. We still run a
// few attempts so the result also has a HINT-detectable pair (covers the
// edge case where structural quirks of `isSelectable` differ slightly
// between selection and matching). Falls back to the best attempt seen.
export function shuffleRemaining(tiles, maxAttempts = 12) {
  let best = _guaranteedShuffle(tiles);
  if (findHint(best) !== null) return best;
  for (let i = 1; i < maxAttempts; i++) {
    best = _guaranteedShuffle(best);
    if (findHint(best) !== null) return best;
  }
  return best;
}
